/**
 * Session dont le RÔLE est celui réellement appliqué par le serveur pour l'espace ouvert —
 * jamais le rôle global du jeton pour les décisions ACTIONNABLES (`canWrite`,
 * `canManageConfiguration`).
 *
 * SUITE DE `useEffectiveNavRoles` (v030-po-ux-01) : le premier lot corrigeait la navigation
 * et la palette (ce qui est VISIBLE). Ce hook ferme le même défaut, un cran plus loin dans
 * la pile, pour ce qui est ACTIONNABLE — un bouton d'écriture affiché à tort n'est pas un
 * problème d'affichage, c'est une action que le serveur refusera (403) après coup.
 *
 * Racine unique, pas 20 correctifs : chaque écran qui appelait `canWrite(useSession())`
 * appelle désormais `canWrite(useEffectiveSession())` — un seul hook recalcule le rôle
 * effectif, les écrans eux-mêmes ne changent pas de logique.
 */
import { useMemo } from 'react'

import { plafonnerAuJeton, type Session } from '@/auth/session'
import { useSession } from '@/hooks/useSession'
import { useWorkspace } from '@/hooks/useWorkspace'
import { rolesEffectifsPourEspace } from '@/hooks/useEffectiveNavRoles'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

export function useEffectiveSession(): Session | null {
  const session = useSession()
  const { activeId: workspaceId } = useWorkspace()

  const membres = useQuery({
    queryKey: ['workspace-members-role-effectif', workspaceId],
    queryFn: () => api.listWorkspaceMembers(workspaceId as string),
    enabled: Boolean(workspaceId) && Boolean(session),
    staleTime: 30_000,
  })

  const monRole = workspaceId && session
    ? (membres.data?.find((m) => m.user_id === session.userId)?.role ?? null)
    : undefined

  return useMemo(() => {
    if (!session) return null
    // FND-V050-083 : l'appartenance (owner du projet) ne dépasse jamais ce que le JETON permet
    // d'écrire côté serveur — sinon des actions affichées sont refusées en 403 après le clic.
    const rolesEffectifs = plafonnerAuJeton(session.roles, rolesEffectifsPourEspace(session.roles, monRole))
    return { ...session, roles: rolesEffectifs }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, workspaceId, monRole])
}
